import { Recipe } from '../types/mealPlan';

// Canonical ingredient-chip vocabulary, mirrored from the backend's
// INGREDIENT_CHIP_DEFINITIONS (services/recipeConstants.ts), the same way
// proteinSuggestions.ts mirrors PROTEIN_INGREDIENT_KEYS. The backend validates
// every submitted key against its own copy, so drift fails safe: an unknown
// key is rejected at the route rather than silently mislearned.

export type IngredientChipRole =
  | 'protein'
  | 'vegetable'
  | 'base'
  | 'dairy'
  | 'accent';

export type IngredientChipDefinition = {
  /** Matches an entry in recipe.ingredientKeys. */
  key: string;
  /** Lowercase human phrase, rendered mid-sentence. */
  display: string;
  role: IngredientChipRole;
  /**
   * Appears in a large share of the catalog (>15% measured), so its presence
   * says little about why THIS dish was rejected. Offered only when the recipe
   * title names it.
   */
  ubiquitous?: boolean;
};

/**
 * The ingredients worth offering as a "don't want this" chip.
 *
 * Curated allow-list, not "every ingredient key minus exclusions". The
 * inclusion rule is: would a person plausibly name this as the reason they
 * don't want the dish? That admits dish-defining components and excludes
 * background seasoning and fat, so "Don't want olive oil" can never render.
 *
 * Proteins are partial on purpose. The main protein already has its own
 * dislike_protein chip; these entries exist to reach SECONDARY proteins, which
 * that chip has never covered.
 */
export const INGREDIENT_CHIP_DEFINITIONS: IngredientChipDefinition[] = [
  // --- Secondary proteins -------------------------------------------------
  { key: 'chicken', display: 'chicken', role: 'protein', ubiquitous: true },
  { key: 'beef', display: 'beef', role: 'protein', ubiquitous: true },
  { key: 'turkey', display: 'turkey', role: 'protein' },
  { key: 'pork', display: 'pork', role: 'protein' },
  { key: 'lamb', display: 'lamb', role: 'protein' },
  { key: 'bacon', display: 'bacon', role: 'protein' },
  { key: 'sausage', display: 'sausage', role: 'protein' },
  { key: 'shrimp', display: 'shrimp', role: 'protein' },
  { key: 'salmon', display: 'salmon', role: 'protein' },
  { key: 'tuna', display: 'tuna', role: 'protein' },
  { key: 'white_fish', display: 'white fish', role: 'protein' },
  { key: 'shellfish', display: 'shellfish', role: 'protein' },
  { key: 'eggs', display: 'eggs', role: 'protein' },
  { key: 'tofu', display: 'tofu', role: 'protein' },
  { key: 'tempeh', display: 'tempeh', role: 'protein' },
  { key: 'seitan', display: 'seitan', role: 'protein' },
  { key: 'plant_based_meat', display: 'plant-based meat', role: 'protein' },
  { key: 'lentils', display: 'lentils', role: 'protein' },
  { key: 'chickpeas', display: 'chickpeas', role: 'protein' },
  { key: 'black_beans', display: 'black beans', role: 'protein' },
  { key: 'kidney_beans', display: 'kidney beans', role: 'protein' },
  { key: 'white_beans', display: 'white beans', role: 'protein' },
  { key: 'beans_generic', display: 'beans', role: 'protein' },

  // --- Vegetables ---------------------------------------------------------
  { key: 'mushrooms', display: 'mushrooms', role: 'vegetable' },
  { key: 'asparagus', display: 'asparagus', role: 'vegetable' },
  { key: 'green_beans', display: 'green beans', role: 'vegetable' },
  { key: 'spinach', display: 'spinach', role: 'vegetable' },
  { key: 'kale', display: 'kale', role: 'vegetable' },
  { key: 'broccoli', display: 'broccoli', role: 'vegetable' },
  { key: 'cauliflower', display: 'cauliflower', role: 'vegetable' },
  { key: 'zucchini', display: 'zucchini', role: 'vegetable' },
  { key: 'eggplant', display: 'eggplant', role: 'vegetable' },
  { key: 'cabbage', display: 'cabbage', role: 'vegetable' },
  { key: 'celery', display: 'celery', role: 'vegetable' },
  { key: 'cucumber', display: 'cucumber', role: 'vegetable' },
  { key: 'lettuce', display: 'salad greens', role: 'vegetable' },
  { key: 'peas', display: 'peas', role: 'vegetable' },
  { key: 'avocado', display: 'avocado', role: 'vegetable' },
  { key: 'carrots', display: 'carrots', role: 'vegetable' },
  { key: 'corn', display: 'corn', role: 'vegetable' },
  { key: 'potatoes', display: 'potatoes', role: 'vegetable' },
  { key: 'sweet_potatoes', display: 'sweet potatoes', role: 'vegetable' },
  { key: 'bell_pepper', display: 'bell peppers', role: 'vegetable', ubiquitous: true },
  { key: 'tomatoes', display: 'tomatoes', role: 'vegetable', ubiquitous: true },
  { key: 'onion', display: 'onions', role: 'vegetable', ubiquitous: true },
  { key: 'red_onion', display: 'red onion', role: 'vegetable' },

  // --- Starch / carb bases ------------------------------------------------
  { key: 'pasta', display: 'pasta', role: 'base' },
  { key: 'quinoa', display: 'quinoa', role: 'base' },
  { key: 'couscous', display: 'couscous', role: 'base' },
  { key: 'brown_rice', display: 'brown rice', role: 'base' },
  { key: 'rice_noodles', display: 'rice noodles', role: 'base' },
  { key: 'wheat_noodles', display: 'wheat noodles', role: 'base' },
  { key: 'tortilla', display: 'tortillas', role: 'base' },
  { key: 'pita', display: 'pita', role: 'base' },
  { key: 'bread', display: 'bread', role: 'base' },
  { key: 'white_rice', display: 'white rice', role: 'base', ubiquitous: true },

  // --- Dairy --------------------------------------------------------------
  { key: 'feta', display: 'feta', role: 'dairy' },
  { key: 'goat_cheese', display: 'goat cheese', role: 'dairy' },
  { key: 'parmesan', display: 'parmesan', role: 'dairy' },
  { key: 'mozzarella', display: 'mozzarella', role: 'dairy' },
  { key: 'cheddar', display: 'cheddar', role: 'dairy' },
  { key: 'cream', display: 'cream', role: 'dairy' },
  { key: 'yogurt', display: 'yogurt', role: 'dairy' },
  { key: 'milk', display: 'milk', role: 'dairy' },
  { key: 'coconut_milk', display: 'coconut milk', role: 'dairy' },
  { key: 'cheese_generic', display: 'cheese', role: 'dairy', ubiquitous: true },

  // --- Dominant accents ---------------------------------------------------
  // Cilantro is 30% of the catalog, which would normally make it ubiquitous,
  // but it is the textbook polarizing ingredient and the aversion is genetic
  // rather than contextual. Frequency is the wrong filter for it.
  { key: 'cilantro', display: 'cilantro', role: 'accent' },
  { key: 'mint', display: 'mint', role: 'accent' },
  { key: 'dill', display: 'dill', role: 'accent' },
  { key: 'curry_powder', display: 'curry powder', role: 'accent' },
  { key: 'curry_paste', display: 'curry paste', role: 'accent' },
  { key: 'fish_sauce', display: 'fish sauce', role: 'accent' },
  { key: 'oyster_sauce', display: 'oyster sauce', role: 'accent' },
  { key: 'miso', display: 'miso', role: 'accent' },
  { key: 'harissa', display: 'harissa', role: 'accent' },
  { key: 'gochujang', display: 'gochujang', role: 'accent' },
  { key: 'tahini', display: 'tahini', role: 'accent' },
  { key: 'peanut_butter', display: 'peanut butter', role: 'accent' },
  { key: 'mayonnaise', display: 'mayonnaise', role: 'accent' },
  { key: 'mustard', display: 'mustard', role: 'accent' },
  { key: 'peanuts', display: 'peanuts', role: 'accent' },
  { key: 'cashews', display: 'cashews', role: 'accent' },
  { key: 'almonds', display: 'almonds', role: 'accent' },
  { key: 'nuts_generic', display: 'nuts', role: 'accent' },
  { key: 'jalapeno', display: 'jalapeño', role: 'accent' },
  { key: 'chipotle', display: 'chipotle', role: 'accent' },
  { key: 'chili_peppers', display: 'chili peppers', role: 'accent' },
  { key: 'balsamic_vinegar', display: 'balsamic', role: 'accent' },
  { key: 'olives', display: 'olives', role: 'accent' },
  { key: 'capers', display: 'capers', role: 'accent' },
  { key: 'ginger', display: 'ginger', role: 'accent', ubiquitous: true },
  { key: 'lemon', display: 'lemon', role: 'accent', ubiquitous: true },
  { key: 'lime', display: 'lime', role: 'accent', ubiquitous: true },
];

const CHIP_BY_KEY = new Map(INGREDIENT_CHIP_DEFINITIONS.map((d) => [d.key, d]));

/** Mirrors the backend cap. Three fits one row on a phone. */
export const MAX_INGREDIENT_CHIPS = 3;

/**
 * Which role gets a slot first when the recipe offers more candidates than the
 * cap. Protein is last because the dish's MAIN protein already has its own
 * chip, so a protein slot here only ever buys a secondary one.
 *
 * This ordering is a starting heuristic, not a measured optimum. Tap rates per
 * role are the thing to tune it against once the chips have shipped.
 */
const ROLE_PRIORITY: IngredientChipRole[] = [
  'vegetable',
  'accent',
  'base',
  'dairy',
  'protein',
];

export const ingredientChipId = (key: string): string =>
  `dislike_ingredient:${key}`;

/** Human phrase for a chip key, or a de-underscored fallback. */
export const ingredientChipDisplay = (key: string): string =>
  CHIP_BY_KEY.get(key)?.display ?? key.replace(/_/g, ' ');

/**
 * Display phrases for the ingredient chips inside a raw selected-tag list,
 * in the order the chips were defined. Used to label the "always avoid" opt-in
 * and to build the terms it writes.
 */
export const selectedIngredientDisplays = (selectedTagIds: string[]): string[] => {
  const displays: string[] = [];
  for (const id of selectedTagIds) {
    const key = ingredientKeyFromChipId(id);
    if (!key) continue;
    const display = ingredientChipDisplay(key);
    if (!displays.includes(display)) displays.push(display);
  }
  return displays;
};

/**
 * Parses a chip id back into its ingredient key, or null when the id isn't an
 * ingredient chip.
 */
export const ingredientKeyFromChipId = (tagId: string): string | null => {
  if (!tagId.startsWith('dislike_ingredient:')) return null;
  const key = tagId.slice('dislike_ingredient:'.length);
  return key.length > 0 ? key : null;
};

const normalizeTerm = (value: string): string =>
  value.trim().toLowerCase().replace(/[_-]+/g, ' ');

/**
 * True when the recipe's NAME references this ingredient. The strongest
 * available prominence signal: a title mention means the ingredient is a
 * headline component rather than a background one, and in the production swap
 * text we sampled it was what the user named four times out of five.
 */
const titleMentions = (recipeName: string, def: IngredientChipDefinition): boolean => {
  const haystack = normalizeTerm(recipeName);
  const display = normalizeTerm(def.display);
  const key = normalizeTerm(def.key);
  const candidates = display === key ? [display] : [display, key];
  for (const term of candidates) {
    if (term.length < 3) continue;
    // Singular/plural tolerance, so "Asparagus" matches an "asparagus" chip and
    // "Green Bean Skillet" matches "green beans".
    const stem = term.replace(/(es|s)$/, '');
    const pattern = new RegExp(`(^|[^a-z])${escapeRegExp(stem)}(es|s)?([^a-z]|$)`);
    if (pattern.test(haystack)) return true;
  }
  return false;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export type IngredientChipOptions = {
  /**
   * The key dislike_protein resolves to, dropped from the ingredient chips so
   * one complaint never renders two chips.
   */
  mainProteinKey?: string | null;
  /**
   * Terms the user has already excluded (dislikes, allergies, avoided
   * proteins). No point asking about something they've told us.
   */
  avoidTerms?: string[];
  /**
   * Keys another chip already on the sheet says the same thing as (today: the
   * ones a rendered format chip covers — see FORMAT_COVERED_INGREDIENT_KEYS in
   * feedbackTags). Dropped before ranking rather than filtered afterwards, so
   * the freed slot backfills with a different ingredient.
   */
  suppressKeys?: string[];
  limit?: number;
};

/**
 * Picks the ingredients worth offering as rejection chips for one recipe.
 *
 * The median catalog recipe matches 10 ingredient keys and the sheet has room
 * for three, so this is mostly a filtering problem. In order: drop keys with no
 * chip definition, the main protein, and anything the user already avoids; keep
 * ubiquitous keys only when the title names them; then fill slots by role, one
 * role at a time, title mentions first.
 */
export const selectIngredientChips = (
  recipe: Pick<Recipe, 'name' | 'ingredientKeys'>,
  options?: IngredientChipOptions
): IngredientChipDefinition[] => {
  const keys = recipe.ingredientKeys;
  if (!keys || keys.length === 0) return [];

  const limit = options?.limit ?? MAX_INGREDIENT_CHIPS;
  const mainProteinKey = options?.mainProteinKey ?? null;
  const avoided = new Set(
    (options?.avoidTerms ?? []).filter(Boolean).map(normalizeTerm)
  );
  const suppressed = new Set(options?.suppressKeys ?? []);

  const seen = new Set<string>();
  const candidates: Array<{
    def: IngredientChipDefinition;
    inTitle: boolean;
    order: number;
  }> = [];

  keys.forEach((key, order) => {
    if (seen.has(key)) return;
    seen.add(key);

    const def = CHIP_BY_KEY.get(key);
    if (!def) return;
    if (key === mainProteinKey) return;
    if (suppressed.has(key)) return;
    if (avoided.has(normalizeTerm(def.display)) || avoided.has(normalizeTerm(def.key))) {
      return;
    }

    const inTitle = titleMentions(recipe.name, def);
    if (def.ubiquitous && !inTitle) return;

    candidates.push({ def, inTitle, order });
  });

  if (candidates.length === 0) return [];

  const rank = (c: (typeof candidates)[number]): number[] => [
    c.inTitle ? 0 : 1,
    ROLE_PRIORITY.indexOf(c.def.role),
    c.order,
  ];
  candidates.sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i++) {
      if (ra[i] !== rb[i]) return ra[i] - rb[i];
    }
    return 0;
  });

  // One slot per role first, so a vegetable-heavy dish can't spend every chip
  // on vegetables and leave its couscous base unofferable. Leftovers backfill
  // any slots still open.
  const picked: IngredientChipDefinition[] = [];
  const usedRoles = new Set<IngredientChipRole>();
  for (const c of candidates) {
    if (picked.length >= limit) break;
    if (usedRoles.has(c.def.role)) continue;
    usedRoles.add(c.def.role);
    picked.push(c.def);
  }
  for (const c of candidates) {
    if (picked.length >= limit) break;
    if (picked.includes(c.def)) continue;
    picked.push(c.def);
  }

  return picked;
};
